import { StatusValue } from "components/Table/StatusValue";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";
import {
  CampaignActivity,
  CampaignVisibility,
  VisibilityTarget,
} from "types/campaigns";
import { OLDate } from "utilities/dateFormat";
import { useStyles } from "../styles";
import { SegmentsFetcher } from "./SegmentsFetcher";
import { TiersFetcher } from "./TiersFetcher";

interface Props {
  defaultLang: DefaultLanguageDataState;
  activity: CampaignActivity;
  active: boolean;
  translations: { locale: string; name: string; description: string }[];
  visibility?: CampaignVisibility;
  displayOrder?: number;
  type?: "campaign" | "leaderboard";
  errors?: any;
}

export const BasicInfo = ({
  defaultLang,
  active,
  activity,
  translations,
  visibility,
  displayOrder,
  type = "campaign",
  errors,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles({});
  const [listOfFetchedElements, setListOfFetchedElements] = useState<any[]>([]);
  const translatedDetails = translations.find(
    (translation) => translation.locale === defaultLang.code,
  );

  const setCampaignVisibility = () => {
    switch (visibility?.target) {
      case VisibilityTarget.TIER:
        return (
          <>
            {message({ id: "tiers" })}:{" "}
            {visibility.tiers?.map((item: string, index) => (
              <TiersFetcher
                id={item}
                key={index}
                setListOfFetchedTiers={(data) =>
                  setListOfFetchedElements((previousArrayState) => [
                    ...previousArrayState,
                    data,
                  ])
                }
              />
            ))}
            {listOfFetchedElements &&
              listOfFetchedElements.map((tier) => (
                <span className={classes.visibilityBox}>{tier.name}</span>
              ))}
          </>
        );
      case VisibilityTarget.SEGMENT:
        return (
          <>
            {message({
              id: "segments",
            })}
            :{" "}
            {visibility.segments?.map((item: string, index) => (
              <SegmentsFetcher
                id={item}
                key={index}
                setListOfFetchedSegments={(data) => {
                  setListOfFetchedElements((previousArrayState) => [
                    ...previousArrayState,
                    data,
                  ]);
                }}
              />
            ))}{" "}
            {listOfFetchedElements &&
              listOfFetchedElements.map((segment) => (
                <span className={classes.visibilityBox}>{segment.name}</span>
              ))}
          </>
        );
      case VisibilityTarget.NONE:
        return message({ id: "hidden" });
      default:
        return message({ id: "visibleForEveryone" });
    }
  };

  const getData = () => {
    switch (type) {
      case "campaign":
        return [
          {
            left: message({ id: "campaignName" }),
            right: translatedDetails?.name,
            hasError: !!errors?.translations?.[0]?.name,
          },
          {
            left: message({ id: "startDate" }),
            right: OLDate(activity?.startsAt || undefined, {
              formatString: "dd MMM, yyyy hh:mm aaaaa'm'",
            }),
            hasError: !!errors?.activity?.startsAt,
          },
          {
            left: message({ id: "endDate" }),
            right: OLDate(activity?.endsAt || undefined, {
              formatString: "dd MMM, yyyy hh:mm aaaaa'm'",
            }),
            hasError: !!errors?.activity?.endsAt,
          },
          {
            left: message({ id: "displayOrder" }),
            right: displayOrder,
            hasError: !!errors?.displayOrder,
          },
          {
            left: message({ id: "campaignDescription" }),
            right: translatedDetails?.description,
            hasError: !!errors?.translations?.[0]?.description,
          },
          {
            left: message({ id: "campaignVisibility" }),
            right: setCampaignVisibility(),
            hasError: !!errors?.visibility,
          },
          {
            left: message({ id: "campaignStatus" }),
            right: <StatusValue status={active} />,
            hasError: false,
          },
        ];
      case "leaderboard":
        return [
          {
            left: message({ id: "name" }),
            right: translatedDetails?.name,
            hasError: !!errors?.translations?.[0]?.name,
          },
          {
            left: message({ id: "description" }),
            right: translatedDetails?.description,
            hasError: !!errors?.translations?.[0]?.description,
          },
          {
            left: message({ id: "timeframe" }),
            right: (
              <div>
                {activity?.startsAt ? (
                  <span>
                    {message({ id: "from" })}{" "}
                    {OLDate(activity?.startsAt, {
                      formatString: "dd MMM yyyy",
                    })}{" "}
                  </span>
                ) : (
                  "-"
                )}
                {activity?.endsAt && (
                  <span>
                    {message({ id: "to" })}{" "}
                    {OLDate(activity?.endsAt, {
                      formatString: "dd MMM yyyy",
                    })}
                  </span>
                )}
              </div>
            ),
            hasError:
              !!errors?.activity?.startsAt || !!errors?.activity?.endsAt,
          },
          {
            left: message({ id: "visibility" }),
            right: setCampaignVisibility(),
            hasError: !!errors?.visibility,
          },
          {
            left: message({ id: "status" }),
            right: <StatusValue status={active} />,
            hasError: !!errors?.active,
          },
        ];
      default:
        return [];
    }
  };

  const data = getData();

  return data.map(({ left, right, hasError }, index) => {
    return (
      <span className={classes.row} key={index}>
        <span
          className={`${classes.left} ${hasError ? classes.summaryError : ""}`}
        >
          {message({ id: left })}
        </span>
        <span className={classes.right}>{right || "-"}</span>
      </span>
    );
  });
};
